"use client";

import { IconPeopleCircle } from "@central-icons-react/round-outlined-radius-3-stroke-2";
import * as React from "react";

import {
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuSearchSection,
  PopoverMenuScrollSection,
  PopoverMenuSection,
} from "@/components/ui/popover-menu";
import type { OrgMember } from "@/lib/org/queries";
import { cn } from "@/lib/utils";


type Props = {
  value: string | null;
  onChange: (memberId: string | null) => void;
  members: OrgMember[];
  trigger: React.ReactElement;
  disabled?: boolean;
};

export function memberLabel(m: OrgMember): string {
  return m.name?.trim() || m.email;
}

function memberInitial(m: OrgMember): string {
  return memberLabel(m).slice(0, 1).toUpperCase();
}

export function MemberAvatar({ member }: { member: OrgMember }) {
  if (member.avatarUrl) {
    // External avatars (Supabase auth metadata, Google OAuth, etc.) — plain
    // <img> on purpose. next/image would gate on configured remote domains
    // and these 16px thumbnails don't need optimization.
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={member.avatarUrl}
        alt=""
        className="size-4 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-neutral-04 text-[9px] font-medium text-neutral-11">
      {memberInitial(member)}
    </span>
  );
}

/**
 * Controlled assignee picker. `value` is the selected user id (or null for
 * unassigned). Parent decides whether `onChange` persists or just updates
 * local form state.
 */
export function TaskAssigneeMenu({
  value,
  onChange,
  members,
  trigger,
  disabled,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const filteredMembers = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const label = memberLabel(m).toLowerCase();
      return label.includes(q) || m.email.toLowerCase().includes(q);
    });
  }, [members, query]);

  const select = (memberId: string | null) => {
    onChange(memberId);
    setOpen(false);
    setQuery("");
  };

  const hasListBelowNoOwner =
    filteredMembers.length > 0 ||
    (!!query.trim() && filteredMembers.length === 0);

  return (
    <PopoverMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
      trigger={trigger}
      menuContentClassName="w-[15.125rem] p-0"
      menu={
        <>
          <PopoverMenuSearchSection
            fullWidthDividerAfter
            inputProps={{
              value: query,
              onChange: (event) => setQuery(event.target.value),
              placeholder: "Search...",
            }}
          />

          <PopoverMenuSection
            fullWidthDividerAfter={hasListBelowNoOwner}
            className={cn("px-1", !hasListBelowNoOwner && "pb-1.5")}
          >
            <PopoverMenuItem
              icon={
                <IconPeopleCircle
                  className="size-4 shrink-0 text-[#777]"
                  aria-hidden
                />
              }
              label="No owner"
              selected={!value}
              disabled={disabled}
              onClick={() => select(null)}
            />
          </PopoverMenuSection>

          {filteredMembers.length > 0 ? (
            <PopoverMenuScrollSection
              className="px-1 pb-1.5"
              maxHeightClassName="max-h-[12rem]"
            >
              {filteredMembers.map((m) => (
                <PopoverMenuItem
                  key={m.id}
                  icon={<MemberAvatar member={m} />}
                  label={memberLabel(m)}
                  selected={m.id === value}
                  disabled={disabled}
                  onClick={() => select(m.id)}
                />
              ))}
            </PopoverMenuScrollSection>
          ) : query.trim() ? (
            <PopoverMenuSection className="px-1 pb-1.5">
              <p className="px-2 py-1.5 text-[12px] text-neutral-07">
                No matches.
              </p>
            </PopoverMenuSection>
          ) : null}
        </>
      }
    />
  );
}
